import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRivalry } from '../contexts/RivalryContext';

/**
 * Main rivalries page - shows list of user's rivalries
 */
export function RivalriesPage() {
  const { rivalries, loading, loadUserRivalries, fighterProfile } = useRivalry();
  const navigate = useNavigate();

  useEffect(() => {
    loadUserRivalries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading rivalries...</div>
      </div>
    );
  }

  const activeRivalries = rivalries.filter((r) => r.status === 'active');
  const completedRivalries = rivalries.filter((r) => r.status === 'completed');

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Your Rivalries</h1>
            <p className="text-gray-400">Compete with friends and track your fitness progress</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/fighter-card"
              className="bg-gray-800 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              View Fighter Card
            </Link>
            <Link
              to="/rivalries/create"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              + Create Rivalry
            </Link>
          </div>
        </div>

        {/* Fighter Profile Summary */}
        {fighterProfile && (
          <div className="bg-gradient-to-r from-gray-800 to-gray-700 rounded-lg p-6 mb-8 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {fighterProfile.profile_image_url ? (
                  <img
                    src={fighterProfile.profile_image_url}
                    alt={fighterProfile.fighter_name}
                    className="w-16 h-16 rounded-full border-2 border-yellow-500"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-700 border-2 border-yellow-500 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-white">{fighterProfile.fighter_name}</h2>
                  {fighterProfile.tagline && <p className="text-gray-400 italic">{fighterProfile.tagline}</p>}
                </div>
              </div>
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">
                    {fighterProfile.record_wins}-{fighterProfile.record_losses}-{fighterProfile.record_draws}
                  </p>
                  <p className="text-sm text-gray-400">Record</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-yellow-500">{fighterProfile.championship_wins}</p>
                  <p className="text-sm text-gray-400">Championships</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {rivalries.length === 0 && (
          <div className="bg-gray-800 rounded-lg p-12 text-center">
            <svg className="w-24 h-24 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-white mb-2">No Rivalries Yet</h2>
            <p className="text-gray-400 mb-6">Create your first rivalry to start competing with friends!</p>
            <Link
              to="/rivalries/create"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Create Your First Rivalry
            </Link>
          </div>
        )}

        {/* Active Rivalries */}
        {activeRivalries.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Active Rivalries ({activeRivalries.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeRivalries.map((rivalry) => (
                <RivalryCard key={rivalry.id} rivalry={rivalry} onClick={() => navigate(`/rivalries/${rivalry.id}`)} />
              ))}
            </div>
          </div>
        )}

        {/* Completed Rivalries */}
        {completedRivalries.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">Completed Rivalries ({completedRivalries.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {completedRivalries.map((rivalry) => (
                <RivalryCard key={rivalry.id} rivalry={rivalry} isCompleted onClick={() => navigate(`/rivalries/${rivalry.id}`)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Individual rivalry card
 */
function RivalryCard({ rivalry, isCompleted, onClick }) {
  const { name, description, current_participants, max_participants, created_at, rivalry_participants } = rivalry;

  // Get user's rank in this rivalry
  const userParticipant = rivalry_participants?.[0];
  const userRank = userParticipant?.current_rank || 0;
  const userPoints = userParticipant?.monthly_points || 0;

  // Calculate rivalry progress
  const participantProgress = (current_participants / max_participants) * 100;

  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-lg overflow-hidden transition-all hover:scale-105 ${
        isCompleted ? 'bg-gray-800 opacity-75' : 'bg-gradient-to-br from-gray-800 to-gray-700'
      }`}
    >
      {/* Header */}
      <div className={`p-4 ${isCompleted ? 'bg-gray-700' : 'bg-gradient-to-r from-blue-600 to-purple-600'}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-1">{name}</h3>
            {description && <p className="text-sm text-gray-200 line-clamp-2">{description}</p>}
          </div>
          {isCompleted && (
            <span className="ml-2 bg-gray-600 text-gray-300 text-xs font-semibold px-2 py-1 rounded">COMPLETED</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        {/* User Stats */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-gray-400 text-sm">Your Rank</p>
            <p className={`text-2xl font-bold ${userRank === 1 ? 'text-yellow-400' : userRank === 2 ? 'text-gray-300' : userRank === 3 ? 'text-orange-400' : 'text-white'}`}>
              {userRank > 0 ? `#${userRank}` : 'Unranked'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-gray-400 text-sm">Your Points</p>
            <p className="text-2xl font-bold text-white">{userPoints}</p>
          </div>
        </div>

        {/* Participants */}
        <div className="mb-2">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-400">Participants</span>
            <span className="text-white font-semibold">
              {current_participants} / {max_participants}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${participantProgress}%` }}
            ></div>
          </div>
        </div>

        {/* Created Date */}
        <p className="text-xs text-gray-500 mt-3">
          Created {new Date(created_at).toLocaleDateString()}
        </p>
      </div>

      {/* Footer */}
      <div className="bg-gray-700 bg-opacity-50 px-4 py-3">
        <button className="w-full text-center text-blue-400 hover:text-blue-300 font-semibold text-sm transition-colors">
          View Dashboard →
        </button>
      </div>
    </div>
  );
}
